import { cloneElement, HTMLAttributes, ReactElement, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Close from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Typography as T,
} from "@mui/material";
import useProjects from "rwb/hooks/useProjects";
import yn from "yn";
import useUserPermission from "~/hooks/useUserPermission";
import useFundingSources from "~/pages/admin/hooks/useFundingSources";

const { VITE_COST_MANAGEMENT_ENABLED, VITE_FUNDING_SOURCE_REQUIRED } = import.meta.env;

type ProjectForm = { title: string; description: string; funding_source?: string };

const CreateForm = (props: { onClose: () => void }) => {
  const navigate = useNavigate();
  const { mutate } = useProjects();
  const { data: fundingSources } = useFundingSources(yn(VITE_COST_MANAGEMENT_ENABLED));
  const [error, setError] = useState("");
  const form = useForm<ProjectForm>({
    defaultValues: { title: "", description: "", funding_source: "" },
    mode: "onChange",
  });
  const isFundingSourceRequired = yn(VITE_FUNDING_SOURCE_REQUIRED, { default: false });

  const onSubmit = async (data: ProjectForm) => {
    const { VITE_RWB_API } = import.meta.env;

    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("description", data.description ?? "");
    if (data.funding_source) {
      formData.append("funding_source", data.funding_source);
    }

    const resp = await fetch(`${VITE_RWB_API}/projects`, {
      method: "POST",
      body: formData,
    });

    if (resp.ok) {
      const id = await resp.json();
      await mutate();
      navigate(`/workbenches/${id}`);
    } else {
      setError("Unable to create workbench");
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <DialogTitle>
        New Workbench
        <IconButton size="small" onClick={props.onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FormControl>
          <FormLabel htmlFor="title">
            <T variant="label">Title*</T>
          </FormLabel>
          <Controller
            control={form.control}
            name="title"
            rules={{
              required: { value: true, message: "Required" },
              maxLength: { value: 255, message: "Title can't exceed 255 characters" },
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <OutlinedInput autoFocus id={field.name} size="small" error={!!error} {...field} />
                <FormHelperText error>{error?.message ?? " "}</FormHelperText>
              </>
            )}
          />
        </FormControl>
        {yn(VITE_COST_MANAGEMENT_ENABLED) && (
          <FormControl>
            <FormLabel htmlFor="funding_source">
              <T variant="label">Funding Source{isFundingSourceRequired ? "*" : ""}</T>
            </FormLabel>
            <Controller
              control={form.control}
              name="funding_source"
              rules={{
                required: {
                  value: isFundingSourceRequired,
                  message: "Required",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  {fundingSources && fundingSources.length > 0 ? (
                    <>
                      <Select
                        id={field.name}
                        size="small"
                        error={!!error}
                        data-testid="funding-source-select"
                        {...field}
                      >
                        {fundingSources?.map((fundingSource) => (
                          <MenuItem key={fundingSource.id} value={fundingSource.id}>
                            {fundingSource.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText error>{error?.message ?? " "}</FormHelperText>
                    </>
                  ) : (
                    <FormHelperText error={isFundingSourceRequired} sx={{ mb: 1 }}>
                      No funding sources found. Contact an administrator to create a funding source.
                    </FormHelperText>
                  )}
                </>
              )}
            />
          </FormControl>
        )}
        <FormControl>
          <FormLabel htmlFor="description">
            <T variant="label">Description</T>
          </FormLabel>
          <Controller
            control={form.control}
            name="description"
            rules={{
              maxLength: { value: 2000, message: "Description can't exceed 2000 characters" },
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <OutlinedInput
                  id={field.name}
                  multiline
                  size="small"
                  rows={5}
                  error={!!error}
                  {...field}
                />
                <FormHelperText error>{error?.message ?? " "}</FormHelperText>
              </>
            )}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} size="medium" color="inherit" variant="outlined">
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          disabled={!form.formState.isValid}
          loading={form.formState.isSubmitting}
          variant="contained"
          size="medium"
        >
          Create
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

const CreateProject = (props: {
  callToAction: ReactElement<HTMLAttributes<HTMLInputElement> & { "data-testid"?: string }>;
}) => {
  const [open, setOpen] = useState(false);
  const canCreate = useUserPermission("create:workbench");
  const handleClose = () => setOpen(false);
  const boundCTA = cloneElement(props.callToAction, {
    onClick: () => setOpen(true),
    "data-testid": "new-project",
  });

  if (!canCreate) {
    return null;
  }

  return (
    <>
      {boundCTA}
      <Dialog
        fullWidth
        open={open}
        onClose={handleClose}
        /**
         * The "title" input should be auto-focused when this
         * dialog opens, but there's an issue that prevents that from
         * happening on dev builds: See this link
         * https://github.com/mui/material-ui/issues/33004
         *
         * Turning on "disableRestoreFocus" is a suitable workaround for
         * dev builds.
         */
        disableRestoreFocus={import.meta.env.DEV}
      >
        <CreateForm onClose={handleClose} />
      </Dialog>
    </>
  );
};

export default CreateProject;
